import * as React from 'react';
import type { TextEffectBlendMode } from '../../types';

type Props = {
  readonly foreground: string;
  readonly background: string;
  readonly mode: TextEffectBlendMode;
  readonly result: string;
};

// Linear Burn is backdrop + source - 1. The other authored modes map directly
// to SVG feBlend modes supported by the Studio and Page Generator renderers.
export function TextEffectBlend({
  foreground,
  background,
  mode,
  result,
}: Props) {
  return mode === 'linear-burn' ? (
    <feComposite
      in={background}
      in2={foreground}
      operator="arithmetic"
      k2={1}
      k3={1}
      k4={-1}
      result={result}
    />
  ) : (
    <feBlend in={foreground} in2={background} mode={mode} result={result} />
  );
}

// Dodge and Linear Burn use effect intensity inside the blend equation.
// Their neutral foregrounds are black and white respectively. Applying the
// mask after a saturated blend clips the paint before its intensity is known.
export function TextEffectMaskedBlend({
  color,
  mask,
  background,
  mode,
  blendResult,
  result,
  useIntensity = true,
}: {
  readonly color: string;
  readonly mask: string;
  readonly background: string;
  readonly mode: TextEffectBlendMode;
  readonly blendResult: string;
  readonly result: string;
  readonly useIntensity?: boolean;
}) {
  const intensityBlend =
    useIntensity && (mode === 'color-dodge' || mode === 'linear-burn');
  const neutral = mode === 'linear-burn' ? 1 : 0;
  const rgb = [1, 3, 5].map(
    (offset) => parseInt(color.slice(offset, offset + 2), 16) / 255
  );
  const foreground = `${result}Color`;
  return (
    <>
      {intensityBlend ? (
        <feColorMatrix
          in={mask}
          values={`${rgb
            .map((channel) => `0 0 0 ${channel - neutral} ${neutral}`)
            .join(' ')} 0 0 0 0 1`}
          result={foreground}
        />
      ) : (
        <feFlood floodColor={color} result={foreground} />
      )}
      <TextEffectBlend
        foreground={foreground}
        background={background}
        mode={mode}
        result={intensityBlend ? result : blendResult}
      />
      {!intensityBlend && (
        <feComposite
          in={blendResult}
          in2={mask}
          operator="in"
          result={result}
        />
      )}
    </>
  );
}
